import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaHistory,
} from "react-icons/fa";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetDataGrid from "./components/KuyumcuDataGrid";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";

import { formatNumberTR } from "./utils/numberFormat";
import { formatEnum as formatIslemTur } from "./utils/enumIslemTurFormat";
import { formatEnum as formatLogTip } from "./utils/LogTipFormat";
import { formatDateTimeTR } from "./utils/FormatDate";

import "./css/KuyumcuIslemGecmisiPage.css";

export default function KuyumcuIslemGecmisiPage() {
  /* ===================== STATE ===================== */

  const [rawLogs, setRawLogs] = useState([]);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [logDetaylar, setLogDetaylar] = useState([]);

  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingDetay, setLoadingDetay] = useState(false);

  /* ===================== GRID ROWS ===================== */

  const logs = useMemo(
    () =>
      (rawLogs || []).map((x) => ({
        ...x,
        id: x.id,
      })),
    [rawLogs]
  );


  /* ===================== GRID COLUMNS ===================== */

  const columns = useMemo(
    () => [
      {
        field: "logTarih",
        headerName: "Log Tarih",
        minWidth: 160,
        renderCell: (p) => formatDateTimeTR(p.row.logTarih),
      },
      {
        field: "islemiYapanAdSoyad",
        headerName: "İşlemi Yapan",
        minWidth: 180,
        flex: 1,
        renderCell: (p) => p.row.islemiYapanAdSoyad || "",
      },
      {
        field: "islemTarih",
        headerName: "İşlem Tarih",
        minWidth: 160,
        renderCell: (p) => formatDateTimeTR(p.row.islemTarih),
      },
      {
        field: "logIslemTipi",
        headerName: "İşlem Tipi",
        width: 130,
        renderCell: (p) => formatLogTip(p.row.logIslemTipi),
      },
      {
        field: "hesapAdi",
        headerName: "Hesap",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "islemTur",
        headerName: "İşlem Türü",
        flex: 1,
        minWidth: 160,
        renderCell: (p) => formatIslemTur(p.row.islemTur),
      },
      {
        field: "birim",
        headerName: "Birim",
        width: 90,
      },
      {
        field: "alinan",
        headerName: "Alınan",
        flex: 1,
        minWidth: 120,
        renderCell: (p) =>
            p.row.alinan != null ? formatNumberTR(p.row.alinan) : "",
      },
      {
        field: "verilen",
        headerName: "Verilen",
        flex: 1,
        minWidth: 120,
        renderCell: (p) =>
            p.row.verilen != null ? formatNumberTR(p.row.verilen) : "",
      },
      {
        field: "aciklama",
        headerName: "Açıklama",
        flex: 2,
        minWidth: 220,
      },
    ],
    []
  );

  /* ===================== LOAD LOGS ===================== */

  const loadLogs = useCallback(async () => {
    try {
      setLoadingLogs(true);
      const userId = getUserId();

      const res = await axiosInstance.get("/islem-log", {
        params: { userId },
      });

      setRawLogs(res.data || []);
    } catch (err) {
      console.error(err);
      setRawLogs([]);
      alert("İşlem geçmişi yüklenemedi.");
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  /* ===================== LOAD DETAIL ===================== */

  const loadLogDetay = useCallback(async (logId) => {
    try {
      setLoadingDetay(true);
      const res = await axiosInstance.get(
        `/islem-log-detay/log/${logId}`
      );
      setLogDetaylar(res.data || []);
    } catch (err) {
      console.error(err);
      setLogDetaylar([]);
      alert("Log detayları yüklenemedi.");
    } finally {
      setLoadingDetay(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  /* ===================== ROW CLICK ===================== */

  const onRowClick = useCallback(
    (params) => {
      const logId = params?.row?.id;
      if (!logId || logId === selectedLogId) return;

      setSelectedLogId(logId);
      loadLogDetay(logId);
    },
    [selectedLogId, loadLogDetay]
  );

  /* ===================== DETAIL GROUP ===================== */

  const eskiKayitlar = useMemo(
    () => logDetaylar.filter((x) => x.durum === "ESKI"),
    [logDetaylar]
  );
  const yeniKayitlar = useMemo(
    () => logDetaylar.filter((x) => x.durum === "YENI"),
    [logDetaylar]
  );
  const tekKayitlar = useMemo(
    () => logDetaylar.filter((x) => x.durum === "TEK"),
    [logDetaylar]
  );

  /* ===================== DETAIL ROW ===================== */

  const renderDetailRow = (d, i) => {
    const isAltinIslem =
      d.islemTur === "ALTIN_ALINDI" || d.islemTur === "ALTIN_VERILDI";

    const isAltinKarsiligiNakit =
      d.islemTur?.startsWith("NAKIT_ISLEM_GRAM");

    return (
      <div key={d.id ?? i} className="ld-row">
        <div className="ld-row-header">
          { d.islemTur?.startsWith("NAKIT") &&
              <div className="ld-row-title">Detaylar</div>

          }

          <div className="ld-row-title">{d.stokAdi}</div>

          {d.islemTur && (
            <span className="ld-islem-badge">
              {formatIslemTur(d.islemTur) || d.islemTur}
            </span>
          )}
        </div>

        <div className="ld-row-meta">
          {/* ORTAK ALANLAR */}
          İşlem Tarih: {formatDateTimeTR(d.islemTarih)}
          {" | "}Birim: {d.birim}
          {" | "}Miktar: {formatNumberTR(d.miktar)}

          {/* ALTIN KARŞILIĞI NAKİT */}
          {isAltinKarsiligiNakit && (
            <>
              {" | "}Güncel Kur: {formatNumberTR(d.guncelKur)}
              {" | "}Para Birimi Kur: {formatNumberTR(d.paraBirimiKur)}
            </>
          )}

          {/* ALTIN (FİZİKİ) */}
          {isAltinIslem && (
            <>
              {" | "}Milyem: {formatNumberTR(d.milyem)}
              {" | "}Adet: {d.adet}
              {" | "}İşçilik: {formatNumberTR(d.iscilik)}
            </>
          )}

          {/* AÇIKLAMA */}
          <>{" | "}Açıklama: {d.aciklama} </>
        </div>

      </div>
    );
  };


  /* ===================== RENDER ===================== */

  return (
    <div className="kuyumcu-layout">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="kuyumcu-content">
        <h1 className="page-title"><FaHistory /> İşlem Geçmişi</h1>

        <div className="ig-grid-wrap">
          <ProsetDataGrid
            rows={logs}
            columns={columns}
            pageSize={10}
            onRowClick={onRowClick}
            noRowsLabel={
              loadingLogs ? "Yükleniyor..." : "Kayıt bulunamadı"
            }
          />
        </div>

        {selectedLogId && (
          <div className="log-detay-container">
            {loadingDetay && (
              <div className="ld-loading">Detaylar yükleniyor…</div>
            )}

            {eskiKayitlar.length > 0 && (
              <div className="ld-panel ld-old">
                <div className="ld-title">ESKİ KAYITLAR</div>
                {eskiKayitlar.map(renderDetailRow)}
              </div>
            )}

            {yeniKayitlar.length > 0 && (
              <div className="ld-panel ld-new">
                <div className="ld-title">YENİ KAYITLAR</div>
                {yeniKayitlar.map(renderDetailRow)}
              </div>
            )}

            {tekKayitlar.length > 0 && (
              <div className="ld-panel ld-tek">
                <div className="ld-title">TEK YÖNLÜ İŞLEM</div>
                {tekKayitlar.map(renderDetailRow)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
